import { scrollPageTo } from './components/pageScroll';
import { startDojoBranding } from './services/dojoBrandingLoader';
import { clearBranding } from './services/dojoBrandingCache';
import React, { useState, useMemo, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAuth, useUser, useClerk, useSignIn, SignIn } from '@clerk/clerk-react';
import BackendService from './services/BackendService';
import notificationService from './services/NotificationService';
import HealthConnectService from './services/HealthConnectService';
import NativePushNotificationService from './services/NativePushNotificationService';
import NativeGoogleSignIn, { NATIVE_OAUTH_STATE_KEY, NATIVE_OAUTH_PENDING_KEY } from './components/NativeGoogleSignIn';
import NativePullToRefresh from './components/NativePullToRefresh';
import { setLocationTrackingEnabled } from './contexts/GeolocationServiceContext';
import { useModal } from './services/ModalService';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import TopBar from './components/TopBar';
import Activities from './components/activities/Activities';
import UserProfileModal from './components/UserProfileModal';
import UserPreferencesModal from './components/UserPreferencesModal';
import UpdatePlanModal from './components/UpdatePlanModal';
import StatsModal from './components/StatsModal';
import ImportExportModal from './components/ImportExportModal';
import InferencesModal from './components/InferencesModal';
import HamburgerMenu from './components/HamburgerMenu';
import ComplianceModal from './components/ComplianceModal';
import FoodsRecipesModal from './components/FoodsRecipesModal';
import StrategyModal from './components/StrategyModal';
import SocialModal from './components/SocialModal';
import DojoHomeModal from './components/DojoHomeModal';
import { publishCoachReactionToast, publishCoachReactionTyping, shouldNotifyCoachReaction } from './components/activities/coachPresence';
import DojoRequirementsModal from './components/DojoRequirementsModal';
import DojoComplianceBanner from './components/DojoComplianceBanner';
import { retainDojoDeviceSetup } from './components/activities/dojoDevicePosition';
import DojoSettingsModal from './components/DojoSettingsModal';
import { useForegroundInterval } from './services/AppStateService';
import { useAppLifecycle } from './services/AppLifecycleService';
import { usePositionCapture } from './services/usePositionCapture';
import useNativeBackgroundPosition from './hooks/useNativeBackgroundPosition';
import nativeBackgroundPosition from './services/NativeBackgroundPositionService';
import { useSelfHostedCamera } from './components/camera/useSelfHostedCamera';
import CameraSwitch from './components/camera/CameraSwitch';
import CameraPanel from './components/camera/CameraPanel';
import OwnerCameraArchive, { OwnerCameraArchiveContext } from './components/camera/OwnerCameraArchive';
import SharedCameraStreams, { SharedCameraStreamsContext } from './components/camera/SharedCameraStreams';
import { useSharedCameraStreams } from './components/camera/useSharedCameraStreams';
import { useOwnerCameraArchive } from './components/camera/useOwnerCameraArchive';
import { reloadToLatest, useAppUpdateAvailable } from './services/AppVersionService';
import { recipeTimerSession } from './components/activities/recipeTimerIdentity';
import RecipeTimerMonitor from './components/activities/RecipeTimerMonitor';
import PricingPage from './pages/PricingPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import SupportPage from './pages/SupportPage';
import DojoInvitePage from './pages/DojoInvitePage';
import LandingPage from './pages/LandingPage';
import AnimatedLogo from './components/AnimatedLogo';
import ConnectionErrorScreen from './components/ConnectionErrorScreen';
import LoadingScreen from './components/LoadingScreen';
import AiBudgetNotice from './components/AiBudgetNotice';
import DailyStatusGate from './components/DailyStatusGate';
import AppUpdateNotice from './components/AppUpdateNotice';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import './App.css';
import ProgressPortrait from './components/ProgressPortrait';
import { getCentralShape, BADGE_STAR_POINTS } from './utils/badgeUtils';
import { BadgeTotemIcon, getBadgeTotemStage } from './components/BadgeTotem';
import UnitToggle from './components/UnitToggle';
import './components/FloatingControls.css';
import FloatingVideo from './components/video/FloatingVideo';
import './App.css';
import { API_BASE_URL } from './config';
import { startBadgeDataLoad } from './badgeDataLoading';
import { profileButtonInitial as resolveProfileButtonInitial } from './components/profileIdentity';
import { loadStartupUserProfile } from './services/startupUserProfile';
import { resolveSettingsDeepLink } from './services/settingsDeepLink';


// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    scrollPageTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Capacitor native-platform check (false in regular browsers / PWA).
const isNativePlatform = () => (
  typeof window !== 'undefined' &&
  !!window.Capacitor &&
  typeof window.Capacitor.isNativePlatform === 'function' &&
  window.Capacitor.isNativePlatform()
);

function App() {
  // Clerk authentication
  const { isLoaded: authLoaded, isSignedIn, getToken, sessionId } = useAuth();
  const { user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const { signIn, setActive: setActiveSession } = useSignIn();
  const latestAppAssets = useAppUpdateAvailable();
  
  const [loading, setLoading] = useState(true);
  const [exitIntent, setExitIntent] = useState(null);
  const [showLoadingScreen, setShowLoadingScreen] = useState(true);
  const [initialViewReady, setInitialViewReady] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [userSynced, setUserSynced] = useState(false);
  const [timerBackendIdentity, setTimerBackendIdentity] = useState(null);
  const [authorizedTimerUserId, setAuthorizedTimerUserId] = useState(null);
  const [syncError, setSyncError] = useState(false);
  const [, setAppName] = useState('');
  const [complianceWeek, setComplianceWeek] = useState(0);
  const [complianceMonth, setComplianceMonth] = useState(0);
  const [complianceYear, setComplianceYear] = useState(0);
  const [sevenYearStarCount, setSevenYearStarCount] = useState(0);
  const [goldYearEquivalents, setGoldYearEquivalents] = useState(0);
  const [complianceLoaded, setComplianceLoaded] = useState(false);
  // Portrait-only nullable data: legacy badge defaults must not invent tiers.
  const [portraitCompliance, setPortraitCompliance] = useState(null);
  const [portraitStarCount, setPortraitStarCount] = useState(null);
  const [portraitOwnerId, setPortraitOwnerId] = useState(null);
  const [complianceLoadFailed, setComplianceLoadFailed] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [isOnline, setIsOnline] = useState(false);
  const [hasOngoingWorkout, setHasOngoingWorkout] = useState(false);
  const [dojoCompliance, setDojoCompliance] = useState(null);
  const [hasDojo, setHasDojo] = useState(false);
  const [dojoChangeVersion, setDojoChangeVersion] = useState(0);
  const [dojoBranding, setDojoBranding] = useState(null);
  
  // New badge component states
  const [volumePRStats, setVolumePRStats] = useState({ 
    week: { prRate: 0, prs: 0, eligible: 0 },
    month: { prRate: 0, prs: 0, eligible: 0 },
    year: { prRate: 0, prs: 0, eligible: 0 }
  });
  const [nutritionCompliance, setNutritionCompliance] = useState({ 
    week: { complianceRate: 0, compliant: 0, total: 0 },
    month: { complianceRate: 0, compliant: 0, total: 0 },
    year: { complianceRate: 0, compliant: 0, total: 0 }
  });

  // Coach activity-reaction event ids already surfaced as a browser
  // notification, so a websocket redelivery/reconnect can't show the same
  // reaction twice. Lives for the life of the app session, same as the
  // notification itself would only ever fire once per real event.
  const notifiedCoachReactionEventIds = useRef(new Set());
  const badgeLoadVersionRef = useRef(0);
  const activityFilterRef = useRef(null);
  const [activityShortcutTarget, setActivityShortcutTarget] = useState(null);
  const [activityFilterSummary, setActivityFilterSummary] = useState('Displaying all activities');

  // Create BackendService with Clerk token getter
  const backendService = useMemo(() => new BackendService(
    () => {
      setLoading(true);
    },
    () => {
      setLoading(false);
    },
    getToken
  ), [getToken]);

  useNativeBackgroundPosition({
    loaded: authLoaded,
    signedIn: isSignedIn,
    login: isSignedIn && clerkUser?.id && sessionId ? `${clerkUser.id}:${sessionId}` : null,
    owner: userSynced && !syncError && timerBackendIdentity?.owner === clerkUser?.id ? timerBackendIdentity.userId : null,
  });

  // Observe transitions before GPS disposal so background telemetry can retain
  // the last foreground fix; ordinary writes cannot read it after the transition.
  useAppLifecycle(authLoaded && isSignedIn && userSynced && !syncError
    && timerBackendIdentity?.owner === clerkUser?.id ? timerBackendIdentity.userId : null, backendService);
  usePositionCapture(authLoaded && isSignedIn && userSynced && !syncError
    && timerBackendIdentity?.owner === clerkUser?.id ? timerBackendIdentity.userId : null, backendService);
  // One camera controller per synced login/owner, above every panel/modal. It
  // never captures on its own; an unsynced owner keeps the switch inert.
  const camera = useSelfHostedCamera({
    backendService,
    loginSessionId: sessionId,
    identity: authLoaded && isSignedIn ? clerkUser?.id || null : null,
    ownerId: authLoaded && isSignedIn && userSynced && !syncError
      && timerBackendIdentity?.owner === clerkUser?.id ? timerBackendIdentity.userId : null,
    mediaOrigin: process.env.REACT_APP_CAMERA_MEDIA_ORIGIN || null,
  });
  const cameraArchive = useOwnerCameraArchive(camera.ownerGeneration);
  const { pathname: cameraArchivePathname } = useLocation();
  const cameraStreams = useSharedCameraStreams(camera.ownerGeneration, cameraArchivePathname === '/', () => activityFilterRef.current?.showOnlyType('media', 'feed'));
  const cameraStreamsRef = useRef(cameraStreams);
  cameraStreamsRef.current = cameraStreams;
  const cameraArchiveRef = useRef(cameraArchive);
  cameraArchiveRef.current = cameraArchive;
  useLayoutEffect(() => {
    // The archive surface exists only on the main route. Close playback on
    // departure without disposing login-scoped deletion debt or live capture.
    if (cameraArchivePathname !== '/') {
      cameraArchiveRef.current.close();
      cameraStreamsRef.current.close();
    }
  }, [cameraArchivePathname]);
  // Render-time identity fence also covers retries resolving before passive cleanup.
  const syncScopeRef = useRef(null);
  if (syncScopeRef.current?.user !== clerkUser || syncScopeRef.current?.signedIn !== isSignedIn
    || syncScopeRef.current?.backend !== backendService) {
    syncScopeRef.current = { user: clerkUser, signedIn: isSignedIn, backend: backendService };
  }
  const retryAttemptRef = useRef(0);
  const cameraRef = useRef(camera);
  cameraRef.current = camera;

  // Use only observable Clerk/session and local controller scope. Token refresh
  // within a session is not a new login. No provider-side revocation is implied.
  const logoutScopeRef = useRef(null);
  const previousLogoutScope = logoutScopeRef.current;
  if (!previousLogoutScope || previousLogoutScope.identity !== clerkUser?.id
    || previousLogoutScope.sessionId !== sessionId || previousLogoutScope.signedIn !== isSignedIn
    || previousLogoutScope.loaded !== authLoaded || previousLogoutScope.backend !== backendService
    || previousLogoutScope.owner !== camera.ownerGeneration) {
    logoutScopeRef.current = { identity: clerkUser?.id, sessionId, signedIn: isSignedIn,
      loaded: authLoaded, backend: backendService, owner: camera.ownerGeneration };
  }
  const logoutScope = logoutScopeRef.current;
  const logoutAttemptRef = useRef(null);
  const logoutMountedRef = useRef(true);
  useLayoutEffect(() => { logoutMountedRef.current = true; return () => { logoutMountedRef.current = false; }; }, []);

  // Layout-phase revocation precedes child passive effects on an account switch.
  // Only the matching backend identity may arm timers after old-scope stop.
  useLayoutEffect(() => {
    if (!authLoaded || (isSignedIn && !clerkUser?.id)) return undefined;
    let cancelled = false;
    const owner = isSignedIn ? clerkUser?.id : null;
    const userId = timerBackendIdentity?.owner === owner ? timerBackendIdentity.userId : null;
    setAuthorizedTimerUserId(null);
    recipeTimerSession.transition(owner, userId).then(result => {
      if (!cancelled && result.ok && userId) setAuthorizedTimerUserId(userId);
    });
    return () => { cancelled = true; };
  }, [authLoaded, isSignedIn, clerkUser?.id, timerBackendIdentity]);

  const handleLogout = useCallback(async () => {
    if (!logoutMountedRef.current || logoutScopeRef.current !== logoutScope || !logoutScope.loaded || !logoutScope.signedIn
      || logoutAttemptRef.current?.scope === logoutScope) return;
    const cameraAtLogout = cameraRef.current;
    const attempt = { scope: logoutScope };
    logoutAttemptRef.current = attempt;
    const isCurrent = () => logoutMountedRef.current && logoutScopeRef.current === logoutScope && logoutAttemptRef.current === attempt;
    // Capture and release the initiating camera before any prerequisite can wait.
    cameraArchiveRef.current.close();
    const restoreStreamsAfterFailedLogout = cameraStreamsRef.current.suspendForLogout();
    void cameraAtLogout.stop();
    let signedOut = false;
    try {
      // Native Stop/detach IPC is dispatched before any logout prerequisite.
      // Refuse sign-out if secure owner detachment cannot be confirmed.
      try {
        const retained = await nativeBackgroundPosition.detach();
        if (isCurrent() && retained?.pending > 0) window.alert(`${retained.pending} unsent location samples remain encrypted on this device for this account. They will not upload while signed out. Sign in to the same account to explicitly recover/flush them.`);
      }
      catch (_) { if (isCurrent()) window.alert('Location capture stopped, but native account detachment could not be confirmed. Please retry sign-out.'); return; }
      if (!isCurrent()) return;
      const recipeResult = await recipeTimerSession.transition(null, null);
      if (!isCurrent()) return;
      if (!recipeResult.ok) {
        window.alert('Could not cancel recipe alarms on this device. Please try signing out again.');
        return;
      }
      if (NativePushNotificationService.isNativeAndroid()) {
        if (!isCurrent()) return;
        try {
          await NativePushNotificationService.unregister(backendService);
        } catch (_) {
          if (isCurrent()) window.alert('Kata.fit could not securely clear and revoke Android notifications. Check your connection and try signing out again.');
          return;
        }
        if (!isCurrent()) return;
      }
      if (!isCurrent()) return;
      await cameraAtLogout.settleForSignOut();
      if (!isCurrent()) return;
      try {
        await signOut();
        if (!isCurrent()) return;
        signedOut = true;
      } catch (_) {
        if (!isCurrent()) return;
        cameraAtLogout.restoreAfterFailedSignOut();
        window.alert('Could not sign out. Your camera has stopped. Please try signing out again.');
      }
    } finally {
      if (!signedOut && isCurrent()) restoreStreamsAfterFailedLogout();
      // Successful callbacks remain consumed until the observed login changes.
      if (!signedOut && logoutAttemptRef.current === attempt) logoutAttemptRef.current = null;
    }
  }, [backendService, signOut, logoutScope]);

  // Sync Clerk user with backend when signed in
  useEffect(() => {
    let cancelled = false;
    if (authLoaded && isSignedIn && clerkUser) {
      setUserSynced(false);
      setSyncError(false);
      backendService.syncClerkUser(clerkUser).then((syncedUser) => {
        if (cancelled || !syncedUser) return;
        setUserSynced(true);
        setTimerBackendIdentity({ owner: clerkUser.id, userId: syncedUser._id || syncedUser.id });
        setLoading(false);
      }).catch(err => {
        if (cancelled) return;
        console.error('Error syncing Clerk user:', err);
        setUserSynced(false);
        setSyncError(true);
        setLoading(false);
      });
    } else if (authLoaded && !isSignedIn) {
      backendService.clearAuthenticatedIdentity();
      setTimerBackendIdentity(null);
      setUserSynced(false);
      setSyncError(false);
      setLoading(false);
    }
    return () => { cancelled = true; };
  }, [authLoaded, isSignedIn, clerkUser, backendService]);

  // Auth + the matching synced owner unlock cosmetic cache hydration only.
  // Membership and authenticated image revalidation stay off the readiness path.
  useLayoutEffect(() => {
    const owner = clerkUser?.id;
    if (!authLoaded || !isSignedIn || !owner || !userSynced || timerBackendIdentity?.owner !== owner) {
      setDojoBranding(null);
      return undefined;
    }
    return startDojoBranding(owner, backendService, setDojoBranding);
  }, [authLoaded, isSignedIn, clerkUser?.id, userSynced, timerBackendIdentity, backendService, dojoChangeVersion]);
  const brandingOwnerRef = useRef(null);
  useLayoutEffect(() => {
    if (!authLoaded) return;
    if (!isSignedIn && brandingOwnerRef.current) void clearBranding(brandingOwnerRef.current);
    brandingOwnerRef.current = isSignedIn ? clerkUser?.id : null;
  }, [authLoaded, isSignedIn, clerkUser?.id]);

  // ------------------------------------------------------------------
  // Native OAuth bridge (Chrome Custom Tabs + deep-link ticket exchange)
  // ------------------------------------------------------------------
  // The native Android app cannot use Clerk's in-WebView Google sign-in
  // because Google blocks embedded WebViews ("disallowed_useragent").
  // NativeGoogleSignIn opens Custom Tabs at kata.fit/?mobile_oauth=1.
  // The PWA running in Chrome mints a single-use Clerk sign-in token via
  // /api/auth/mobile-ticket and redirects to fit.kata.mobile://oauth-callback
  // with the ticket. Android delivers that URL to the app via appUrlOpen;
  // we exchange the ticket for a session inside the WebView here.

  // 1) (Native only) Listen for the deep-link callback and claim the session.
  useEffect(() => {
    if (!isNativePlatform()) return;
    const App = window.Capacitor?.Plugins?.App;
    if (!App || !App.addListener) return;

    let listenerHandle;
    let cancelled = false;

    const handler = async (event) => {
      try {
        if (!event || !event.url) return;
        const url = String(event.url);
        if (!url.startsWith('fit.kata.mobile://oauth-callback')) return;

        // Parse ticket + state from the URL. Custom-scheme URLs aren't always
        // accepted by URL(), so do it by hand.
        const queryIndex = url.indexOf('?');
        const params = new URLSearchParams(queryIndex >= 0 ? url.slice(queryIndex + 1) : '');
        const ticket = params.get('ticket');
        const incomingState = params.get('state');
        const expectedState = (() => {
          try { return localStorage.getItem(NATIVE_OAUTH_STATE_KEY); } catch (_) { return null; }
        })();

        // Best-effort: close Custom Tabs whether or not the ticket validates.
        try {
          const Browser = window.Capacitor?.Plugins?.Browser;
          if (Browser && Browser.close) await Browser.close();
        } catch (_) { /* ignore */ }

        if (!ticket) {
          console.warn('OAuth deep link missing ticket');
          return;
        }
        if (expectedState && incomingState && expectedState !== incomingState) {
          console.warn('OAuth state mismatch; ignoring callback');
          return;
        }

        try { localStorage.removeItem(NATIVE_OAUTH_STATE_KEY); } catch (_) {}
        try { localStorage.removeItem(NATIVE_OAUTH_PENDING_KEY); } catch (_) {}

        if (!signIn || !setActiveSession) {
          console.warn('Clerk signIn not yet loaded; cannot consume ticket');
          return;
        }

        const result = await signIn.create({ strategy: 'ticket', ticket });
        if (result && result.status === 'complete' && result.createdSessionId) {
          await setActiveSession({ session: result.createdSessionId });
        } else {
          console.warn('signIn.create returned non-complete status:', result?.status);
        }
      } catch (err) {
        console.error('Error handling OAuth deep link:', err);
      }
    };

    (async () => {
      try {
        listenerHandle = await App.addListener('appUrlOpen', handler);
      } catch (err) {
        console.error('Failed to register appUrlOpen listener:', err);
      }
      // Also check for a URL the app may have been opened with.
      try {
        if (App.getLaunchUrl) {
          const launch = await App.getLaunchUrl();
          if (!cancelled && launch && launch.url) handler({ url: launch.url });
        }
      } catch (_) { /* ignore */ }
    })();

    return () => {
      cancelled = true;
      try {
        if (listenerHandle && listenerHandle.remove) listenerHandle.remove();
      } catch (_) { /* ignore */ }
    };
  }, [signIn, setActiveSession]);

  // 2) (Browser side, running in Chrome Custom Tabs) When the URL includes
  //    ?mobile_oauth=1 and we have a Clerk session, mint a ticket and
  //    deep-link back into the native app.
  useEffect(() => {
    if (isNativePlatform()) return; // only the Chrome side does the redirect
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (_) { return; }
    if (params.get('mobile_oauth') !== '1') return;
    if (!authLoaded || !isSignedIn) return;

    let cancelled = false;
    (async () => {
      try {
        const state = params.get('state') || '';
        const { ticket } = await backendService.getMobileSignInTicket();
        if (cancelled || !ticket) return;
        const target =
          `fit.kata.mobile://oauth-callback?ticket=${encodeURIComponent(ticket)}` +
          `&state=${encodeURIComponent(state)}`;
        window.location.href = target;
      } catch (err) {
        console.error('Failed to mint mobile sign-in ticket:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [authLoaded, isSignedIn, backendService]);

  // 3) (Browser side) Auto-show the Clerk sign-in UI when arriving via the
  //    mobile_oauth handoff so the user doesn't have to tap through the
  //    landing page.
  useEffect(() => {
    if (isNativePlatform()) return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('mobile_oauth') === '1' && !isSignedIn) {
        setShowSignIn(true);
      }
    } catch (_) { /* ignore */ }
  }, [isSignedIn]);

  const retrySync = () => {
    const scope = syncScopeRef.current;
    const attempt = ++retryAttemptRef.current;
    const isCurrent = () => syncScopeRef.current === scope && retryAttemptRef.current === attempt;
    setUserSynced(false);
    setLoading(true);
    setSyncError(false);
    backendService.syncClerkUser(clerkUser).then((syncedUser) => {
      if (!syncedUser) return;
      if (!isCurrent()) return;
      setTimerBackendIdentity({ owner: clerkUser.id, userId: syncedUser._id || syncedUser.id });
      setUserSynced(true);
      setLoading(false);
    }).catch(err => {
      if (!isCurrent()) return;
      console.error('Error syncing Clerk user:', err);
      setUserSynced(false);
      setSyncError(true);
      setLoading(false);
    });
  };
  
  // The static HTML loading surface owns the time before React can paint. Remove
  // it only after this tree commits; the React loading screen is already visible
  // underneath, so there is no empty frame during the handoff.
  useLayoutEffect(() => {
    document.getElementById('boot-loading-screen')?.remove();
  }, []);

  const handleInitialViewReady = useCallback(() => {
    setInitialViewReady(true);
  }, []);

  // Signed-out and sync-error destinations are visible as soon as App renders.
  // Signed-in startup is released by DailyStatusGate after it resolves to either
  // the readiness prompt or the app shell.
  useEffect(() => {
    if (!loading && (!isSignedIn || syncError)) {
      handleInitialViewReady();
    }
  }, [loading, isSignedIn, syncError, handleInitialViewReady]);

  // Smooth loading screen exit transition after the destination is paintable.
  const splashOwner = isSignedIn ? clerkUser?.id : null;
  const previousSplashOwner = useRef(splashOwner);
  useLayoutEffect(() => {
    if (previousSplashOwner.current === splashOwner) return;
    previousSplashOwner.current = splashOwner;
    setShowLoadingScreen(true);
    setInitialViewReady(false);
    setExitIntent(null);
  }, [splashOwner]);
  const ownerReady = authLoaded && isSignedIn && userSynced && !syncError && clerkUser?.id
    && timerBackendIdentity?.owner === clerkUser.id && Boolean(timerBackendIdentity.userId);
  const destinationReady = authLoaded && ((!isSignedIn && !loading) || (syncError && !loading) || ownerReady);
  const isLoadingExiting = Boolean(initialViewReady && destinationReady && exitIntent?.owner === splashOwner);
  useEffect(() => {
    // Branding is cosmetic and continues loading independently of the destination.
    if (!initialViewReady || !destinationReady) return;
    setExitIntent({ owner: splashOwner });
  }, [initialViewReady, destinationReady, splashOwner]);
  useEffect(() => {
    if (!isLoadingExiting) return undefined;
    const timer = setTimeout(() => {
      setShowLoadingScreen(false);
    }, 600); // Match the CSS transition duration
    return () => clearTimeout(timer);
  }, [isLoadingExiting, splashOwner]);

  const modalService = useModal();

  // Update document title based on active modal
  useEffect(() => {
    if (modalService.modals.length > 0) {
      const activeModal = modalService.modals[modalService.modals.length - 1];
      const componentName = activeModal.ModalComponent.displayName || activeModal.ModalComponent.name;
      
      const titleMap = {
        'NewActivityModal': 'New Activity',
        'RecommendationsModal': 'Insight',
        'StrategyModal': 'Strategy & Plan',
        'CoachHubModal': 'Your Coach',
        'UserProfileModal': 'Profile',
        'StatsModal': 'Statistics',
        'UpdatePlanModal': 'Plan Settings',
        'ImportExportModal': 'Import/Export',
        'InferencesModal': 'Analysis',
        'ComplianceModal': 'Badge',
        'BadgeLevelModal': 'Badge',
        'FoodSelectionModal': 'Log Food',
        'FoodsRecipesModal': 'Foods & Recipes',
        'SocialModal': 'Social',
        'DojoHomeModal': 'Dojo',
        'DojoSettingsModal': 'Dojo Settings',
        'DojoRequirementsModal': 'Dojo Rules',
        'MetricHistoryModal': 'History'
      };
      
      let title = titleMap[componentName] || 'Kata.fit';

      // Special case for SocialModal to show active tab
      if (componentName === 'SocialModal') {
          const tab = activeModal.props?.initialTab || 'Social';
          // Capitalize first letter
          title = tab.charAt(0).toUpperCase() + tab.slice(1);
          // Map specific tab names if needed
          if (title === 'Messages') title = 'Chat';
          if (title === 'Dojo') title = 'Dojo';
      }

      document.title = `${title} | Kata.fit`;
    }
  }, [modalService.modals]);
  
  // Monitor WebSocket connection status for online indicator
  const checkWebSocketStatus = useCallback(() => {
    const ws = window.appWebSocket;
    setIsOnline(ws && ws.readyState === WebSocket.OPEN);
  }, []);

  useEffect(() => {
    checkWebSocketStatus();
  }, [isSignedIn, checkWebSocketStatus]);

  useForegroundInterval(checkWebSocketStatus, 1000);

  // Badge Debug Controls - exposed to browser console for testing badge variants
  useEffect(() => {
    // Only expose in development mode
    if (process.env.NODE_ENV === 'development') {
      window.badgeDebug = {
        // Discipline compliance (central shape)
        setCompliance: (week, month, year) => {
          if (week !== undefined) setComplianceWeek(week);
          if (month !== undefined) setComplianceMonth(month);
          if (year !== undefined) setComplianceYear(year);
          console.log(`🎯 Compliance set: week=${week ?? complianceWeek}, month=${month ?? complianceMonth}, year=${year ?? complianceYear}`);
        },
        
        // Volume PR stats (arrows)
        setVolumePRs: (week, month, year) => {
          setVolumePRStats(prev => ({
            week: { ...prev.week, prRate: week ?? prev.week.prRate },
            month: { ...prev.month, prRate: month ?? prev.month.prRate },
            year: { ...prev.year, prRate: year ?? prev.year.prRate }
          }));
          console.log(`📈 Volume PRs set: week=${week ?? 'unchanged'}, month=${month ?? 'unchanged'}, year=${year ?? 'unchanged'}`);
        },
        
        // Nutrition compliance (temple)
        setNutrition: (week, month, year) => {
          setNutritionCompliance(prev => ({
            week: { ...prev.week, complianceRate: week ?? prev.week.complianceRate },
            month: { ...prev.month, complianceRate: month ?? prev.month.complianceRate },
            year: { ...prev.year, complianceRate: year ?? prev.year.complianceRate }
          }));
          console.log(`🏛️ Nutrition set: week=${week ?? 'unchanged'}, month=${month ?? 'unchanged'}, year=${year ?? 'unchanged'}`);
        },
        
        // Star count (7-year progress)
        setStars: (count, goldEquivalents) => {
          if (count !== undefined) setSevenYearStarCount(count);
          if (goldEquivalents !== undefined) setGoldYearEquivalents(goldEquivalents);
          console.log(`⭐ Stars set: count=${count ?? sevenYearStarCount}, goldEquivalents=${goldEquivalents ?? goldYearEquivalents}`);
        },
        
        // Preset configurations for quick testing
        presets: {
          // All components at 0
          reset: () => {
            window.badgeDebug.setCompliance(0, 0, 0);
            window.badgeDebug.setVolumePRs(0, 0, 0);
            window.badgeDebug.setNutrition(0, 0, 0);
            window.badgeDebug.setStars(0, 0);
            console.log('🔄 Badge reset to zero');
          },
          
          // All components maxed out
          max: () => {
            window.badgeDebug.setCompliance(100, 100, 100);
            window.badgeDebug.setVolumePRs(100, 100, 100);
            window.badgeDebug.setNutrition(100, 100, 100);
            window.badgeDebug.setStars(8, 12);
            console.log('🌟 Badge maxed out!');
          },
          
          // Test each tier level (30, 50, 70, 90)
          tier1: () => {
            window.badgeDebug.setCompliance(35, 35, 35);
            window.badgeDebug.setVolumePRs(35, 35, 35);
            window.badgeDebug.setNutrition(35, 35, 35);
            window.badgeDebug.setStars(1, 1);
            console.log('📊 Tier 1 (30%+)');
          },
          tier2: () => {
            window.badgeDebug.setCompliance(55, 55, 55);
            window.badgeDebug.setVolumePRs(55, 55, 55);
            window.badgeDebug.setNutrition(55, 55, 55);
            window.badgeDebug.setStars(2, 2);
            console.log('📊 Tier 2 (50%+)');
          },
          tier3: () => {
            window.badgeDebug.setCompliance(75, 75, 75);
            window.badgeDebug.setVolumePRs(75, 75, 75);
            window.badgeDebug.setNutrition(75, 75, 75);
            window.badgeDebug.setStars(4, 4);
            console.log('📊 Tier 3 (70%+)');
          },
          tier4: () => {
            window.badgeDebug.setCompliance(95, 95, 95);
            window.badgeDebug.setVolumePRs(95, 95, 95);
            window.badgeDebug.setNutrition(95, 95, 95);
            window.badgeDebug.setStars(6, 8);
            console.log('📊 Tier 4 (90%+)');
          },
          
          // Test individual components in isolation
          templeOnly: (level = 95) => {
            window.badgeDebug.presets.reset();
            window.badgeDebug.setNutrition(level, level, level);
            console.log(`🏛️ Temple only at ${level}%`);
          },
          arrowsOnly: (level = 95) => {
            window.badgeDebug.presets.reset();
            window.badgeDebug.setVolumePRs(level, level, level);
            console.log(`📈 Arrows only at ${level}%`);
          },
          complianceOnly: (level = 95) => {
            window.badgeDebug.presets.reset();
            window.badgeDebug.setCompliance(level, level, level);
            console.log(`🎯 Compliance only at ${level}%`);
          }
        },
        
        // Help text
        help: () => {
          console.log(`
╔══════════════════════════════════════════════════════════════╗
║                    🏆 BADGE DEBUG CONTROLS                   ║
╠══════════════════════════════════════════════════════════════╣
║ INDIVIDUAL CONTROLS:                                         ║
║   badgeDebug.setCompliance(week, month, year)  → Central shape║
║   badgeDebug.setVolumePRs(week, month, year)   → Arrows       ║
║   badgeDebug.setNutrition(week, month, year)   → Temple       ║
║   badgeDebug.setStars(count, goldEquivalents)  → Star halo    ║
║                                                              ║
║ PRESETS:                                                     ║
║   badgeDebug.presets.reset()      → All to 0                 ║
║   badgeDebug.presets.max()        → All to 100               ║
║   badgeDebug.presets.tier1()      → All at 30%+ level        ║
║   badgeDebug.presets.tier2()      → All at 50%+ level        ║
║   badgeDebug.presets.tier3()      → All at 70%+ level        ║
║   badgeDebug.presets.tier4()      → All at 90%+ level        ║
║                                                              ║
║ ISOLATION TESTS:                                             ║
║   badgeDebug.presets.templeOnly(level)                       ║
║   badgeDebug.presets.arrowsOnly(level)                       ║
║   badgeDebug.presets.complianceOnly(level)                   ║
║                                                              ║
║ TIER THRESHOLDS:                                             ║
║   0-29%  = Hidden/None                                       ║
║   30-49% = Tier 1 (Basic)                                    ║
║   50-69% = Tier 2 (Intermediate)                             ║
║   70-89% = Tier 3 (Advanced)                                 ║
║   90%+   = Tier 4 (Mastery)                                  ║
╚══════════════════════════════════════════════════════════════╝
          `);
        }
      };
      
      console.log('🎮 Badge debug controls available. Type badgeDebug.help() for commands.');
    }
    
    return () => {
      if (window.badgeDebug) {
        delete window.badgeDebug;
      }
    };
  }, [complianceWeek, complianceMonth, complianceYear, sevenYearStarCount, goldYearEquivalents]);

  // Fetch app config on mount
  useEffect(() => {
    backendService.fetchAppConfig().then(config => {
      setAppName(config.appName);
      document.title = config.appName;
    });
  }, [backendService]);

  // Native push binds only after authentication; it checks existing permission
  // and registers when already granted, but never prompts automatically.
  useEffect(() => {
    if (!isSignedIn || !userSynced || !NativePushNotificationService.isNativeAndroid()) return undefined;
    NativePushNotificationService.bindAuthenticatedUser(backendService, clerkUser?.id).catch(() => {});
    return undefined;
  }, [isSignedIn, userSynced, backendService, clerkUser?.id]);

  // Health Connect: auto-sync when the app comes to the foreground (Android wrapper only).
  // Debounced to once per 10 minutes.
  useEffect(() => {
    if (!isSignedIn || !userSynced || !HealthConnectService.isNative()) return undefined;
    const MIN_INTERVAL_MS = 10 * 60 * 1000;
    let lastAttempt = 0;
    let inFlight = false;

    const maybeSync = async () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (inFlight || now - lastAttempt < MIN_INTERVAL_MS) return;
      lastAttempt = now;
      inFlight = true;
      try {
        const av = await HealthConnectService.isAvailable();
        const permissionStatus = av.available
          ? await HealthConnectService.getPermissionStatus()
          : { grantedTypes: [] };
        const lastSyncAt = await HealthConnectService.getLastSyncAt();
        await backendService.updateHealthConnectStatus({
          available: !!av.available,
          status: av.status || av.reason || null,
          granted_types: permissionStatus.grantedTypes,
          last_sync_at: lastSyncAt
        }).catch((error) => console.warn('[HealthConnect] could not publish status:', error));
        if (!av.available) {
          console.warn('[HealthConnect] auto-sync skipped, unavailable:', av.status || av.reason);
          return;
        }
        // Sync whatever is granted rather than requiring the full set. This
        // gate used to be `hasAllPermissions()`, which meant a single revoked
        // type silently killed weight and sleep syncing together —
        // and Health Connect drops every grant when the app is reinstalled.
        // syncRecent() already reads only the granted subset.
        const { hasAll, grantedTypes } = permissionStatus;
        if (grantedTypes.length === 0) {
          console.warn('[HealthConnect] auto-sync skipped, no read permissions granted');
          return;
        }
        if (!hasAll) {
          console.warn('[HealthConnect] syncing partial permission set:', grantedTypes.join(', '));
        }
        const result = await HealthConnectService.syncRecent(backendService);
        if (!result?.ok) {
          console.warn('[HealthConnect] auto-sync did not run:', result?.reason);
        } else {
          await backendService.updateHealthConnectStatus({
            available: true,
            status: av.status || null,
            granted_types: grantedTypes,
            last_sync_at: await HealthConnectService.getLastSyncAt()
          }).catch((error) => console.warn('[HealthConnect] could not publish status:', error));
        }
      } catch (err) {
        console.warn('[HealthConnect] auto-sync failed:', err?.message || err);
      } finally {
        inFlight = false;
      }
    };

    // Run once shortly after mount, then on each foreground.
    const initialTimer = setTimeout(maybeSync, 2000);
    document.addEventListener('visibilitychange', maybeSync);
    return () => {
      clearTimeout(initialTimer);
      document.removeEventListener('visibilitychange', maybeSync);
    };
  }, [isSignedIn, userSynced, backendService]);
  
  // State for email verification message
  const [verificationMessage, setVerificationMessage] = useState(null);
  
  // Handle email verification token from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const path = window.location.pathname;
    
    if (path === '/verify-email' && token) {
      // Call backend to verify the token
      const verifyEmail = async () => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/email-verification/verify?token=${token}`
          );
          const data = await response.json();
          
          if (response.ok && data.success) {
            setVerificationMessage({ type: 'success', text: 'Email verified successfully! You can now close this tab or continue using the app.' });
          } else {
            setVerificationMessage({ type: 'error', text: data.error || 'Email verification failed. The link may have expired.' });
          }
        } catch (error) {
          setVerificationMessage({ type: 'error', text: 'Email verification failed. Please try again.' });
        }
        
        // Clean up URL
        window.history.replaceState({}, document.title, '/');
      };
      
      verifyEmail();
    }
  }, []);

  // Handle direct links to the authoritative owner surface.
  useEffect(() => {
    if (!isSignedIn || !userSynced) return;
    const target = resolveSettingsDeepLink(window.location.search);
    if (target) {
      // Clean up URL first
      window.history.replaceState({}, document.title, '/');
      if (target.owner === 'dojo') {
        modalService.open(DojoSettingsModal, {
          backendService,
          onDojoChange: () => setDojoChangeVersion(version => version + 1),
          scrollToSection: target.section,
        });
      } else {
        modalService.open(UserPreferencesModal, {
          backendService,
          scrollToSection: target.section,
        });
      }
    }
  }, [isSignedIn, userSynced, backendService, modalService]);
  
  // Fetch unread message count
  const fetchUnreadCount = useCallback(async () => {
    if (!isSignedIn) return;
    const count = await backendService.getUnreadMessageCount();
    setUnreadMessageCount(count);
  }, [isSignedIn, backendService]);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Paused while backgrounded; the resume tick refreshes the badge on return.
  useForegroundInterval(fetchUnreadCount, isSignedIn ? 30000 : null);
  
  // Load notification preferences and listen for WebSocket notifications
  useEffect(() => {
    if (!isSignedIn) return;
    
    let currentUserId = null;
    // Load notification preferences
    const loadNotificationPrefs = async () => {
      try {
        const userData = await loadStartupUserProfile(backendService);
        currentUserId = userData?._id || userData?.id || null;
        if (userData.notification_preferences) {
          notificationService.setPreferences(userData.notification_preferences);
        }
        // Sync location tracking setting to localStorage for GeolocationServiceProvider
        setLocationTrackingEnabled(userData.location_tracking === true);
      } catch (error) {
        console.error('Error loading notification preferences:', error);
      }
    };
    
    loadNotificationPrefs();
    
    // Listen for WebSocket messages to trigger browser notifications
    const handleWebSocketMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Shared dojo coach settings changed: invalidate profile/feed state for
        // every connected member so name and avatars update without reload.
        if (data.type === 'dojo_settings_updated' || data.type === 'coach_settings_updated') {
          setDojoChangeVersion(version => version + 1);
        } else if (data.type === 'new_message') {
          if (currentUserId && String(data.userId) !== String(currentUserId)) return;
          const sender = data.message.from_user || {};
          notificationService.showMessageNotification(
            sender.display_name || sender.email || 'Someone',
            data.message.content?.substring(0, 100),
            () => {
              // Open social modal to messages when clicked
              modalService.open((props) => (
                <SocialModal 
                  streams={cameraStreamsRef.current}
                  {...props} 
                  backendService={backendService}
                  initialTab="messages"
                  initialFriendId={data.message.from_user_id}
                />
              ));
            }
          );
          // Update unread count
          setUnreadMessageCount(prev => prev + 1);
        } else if (data.type === 'new_dojo_message') {
          if (currentUserId && Array.isArray(data.memberIds) && !data.memberIds.map(String).includes(String(currentUserId))) return;
          const sender = data.message.from_user || {};
          notificationService.showDojoMessageNotification(
            data.dojoName || 'Your Dojo',
            sender.display_name || sender.email || 'Someone',
            data.message.content?.substring(0, 100),
            () => {
              modalService.open((props) => (
                <DojoHomeModal
                  {...props}
                  backendService={backendService}
                  initialDojoChat
                />
              ));
            }
          );
        } else if (data.type === 'friend_request') {
          notificationService.showFriendRequestNotification(
            data.from_user?.display_name || data.from_user?.email || 'Someone',
            () => {
              modalService.open((props) => (
                <SocialModal 
                  streams={cameraStreamsRef.current}
                  {...props} 
                  backendService={backendService}
                  initialTab="requests"
                />
              ));
            }
          );
        } else if (data.type === 'dojo:requirements_updated') {
          // Another member (usually the chief) changed the rule. Recheck the
          // feed banner rather than waiting for a reload or local modal save.
          setDojoChangeVersion(version => version + 1);
        } else if (data.type === 'dojo_invite') {
          notificationService.showDojoInviteNotification(
            data.dojo_name || 'A Dojo',
            () => {
              modalService.open((props) => (
                <DojoHomeModal
                 {...props}
                 backendService={backendService}
                />
              ));
            }
          );
        } else if (data.type === 'coach_reaction_received') {
          // Publish into the shared top-toast stack so a reaction is visible
          // anywhere in the app without requiring a system notification.
          if (data.replyWorthwhile !== true && shouldNotifyCoachReaction(notifiedCoachReactionEventIds.current, data.eventId)) {
            publishCoachReactionToast({
              eventId: data.eventId,
              activityId: data.activityId || null,
              activityName: data.activityName || 'activity',
              reaction: data.reaction,
            });
          }
        } else if (data.type === 'coach_typing_started') {
          notificationService.showCoachTypingNotification();
          publishCoachReactionTyping(data.eventId, true);
        } else if (data.type === 'coach_typing_stopped') {
          publishCoachReactionTyping(data.eventId, false);
        }
      } catch (e) {
        // Not JSON or not relevant
      }
    };
    
    // Wait for WebSocket to be available
    const setupWebSocketListener = () => {
      if (window.appWebSocket) {
        window.appWebSocket.addEventListener('message', handleWebSocketMessage);
      }
    };
    
    setupWebSocketListener();
    // Also check periodically in case WebSocket connects later
    const wsCheckInterval = setInterval(() => {
      if (window.appWebSocket && !window.appWebSocket._notificationListenerAdded) {
        window.appWebSocket.addEventListener('message', handleWebSocketMessage);
        window.appWebSocket._notificationListenerAdded = true;
      }
    }, 1000);
    
    return () => {
      clearInterval(wsCheckInterval);
      if (window.appWebSocket) {
        window.appWebSocket.removeEventListener('message', handleWebSocketMessage);
        window.appWebSocket._notificationListenerAdded = false;
      }
    };
  }, [isSignedIn, backendService, modalService]);
  
  // REMOVED: Auto-popup of recommendations. Moved to "notification" model in Activities.js

  const computeSevenYearProgress = useCallback((stats) => {
    if (!Array.isArray(stats)) {
      return { stars: 0, goldYears: 0 };
    }

    const complianceCredits = stats.reduce((sum, stat) => {
      if (!stat) {
        return sum;
      }
      const totalItems = typeof stat.total_plan_items === 'number' ? stat.total_plan_items : 0;
      const completedItems = typeof stat.plan_items_completed === 'number' ? stat.plan_items_completed : 0;
      if (totalItems <= 0) {
        return sum;
      }
      const rawRatio = completedItems / Math.max(1, totalItems);
      const boundedRatio = Math.min(1, Math.max(0, rawRatio));
      return sum + boundedRatio;
    }, 0);

    const goldYearUnits = complianceCredits / (52 * 0.9);
    const completedGoldYears = Math.max(0, Math.floor(goldYearUnits));
    const stars = Math.min(12, Math.max(0, completedGoldYears - 1));

    return { stars, goldYears: goldYearUnits };
  }, []);

  const fetchActivityStats = useCallback(() => {
    const requestVersion = ++badgeLoadVersionRef.current;
    setPortraitOwnerId(clerkUser?.id);
    setPortraitCompliance(null);
    setPortraitStarCount(null);
    setComplianceLoadFailed(false);
    const isCurrent = () => requestVersion === badgeLoadVersionRef.current;
    const endDate = new Date();
    const startDate = subtractWeeks(new Date(), 52 * 7);
    const load = startBadgeDataLoad({
      backendService,
      startDate,
      endDate,
      onActivityStats: data => {
        const { stars: computedStars, goldYears } = computeSevenYearProgress(data);
        setSevenYearStarCount(computedStars);
        setPortraitStarCount(Array.isArray(data) ? computedStars : null);
        setGoldYearEquivalents(goldYears);
      },
      onCompliance: complianceResults => {
        setPortraitCompliance({
          week: complianceResults[7]?.rate ?? null,
          month: complianceResults[30]?.rate ?? null,
          year: complianceResults[365]?.rate ?? null,
        });
        setComplianceWeek(complianceResults[7]?.rate ?? 0);
        setComplianceMonth(complianceResults[30]?.rate ?? 0);
        setComplianceYear(complianceResults[365]?.rate ?? 0);
      },
      onBadgeStats: badgeStats => {
        if (badgeStats?.volumePRs) setVolumePRStats(badgeStats.volumePRs);
        if (badgeStats?.nutritionCompliance) setNutritionCompliance(badgeStats.nutritionCompliance);
      },
      onOptionalError: (source, error) => {
        console.error(`Error fetching ${source}:`, error);
      },
      isCurrent,
    });

    // Never render the default zero-rate potato as if it were authoritative.
    // Compliance alone owns readiness; seven-year and detail data can settle
    // independently without blocking the user's earned central totem.
    load.complianceReady
      .then(() => {
        if (isCurrent()) setComplianceLoaded(true);
      })
      .catch(error => {
        if (isCurrent()) {
          setComplianceLoadFailed(true);
          console.error('Error fetching compliance from backend:', error);
        }
      });
    return load.settled;
  }, [backendService, computeSevenYearProgress, clerkUser?.id]);

  useEffect(() => {
    if (isSignedIn && userSynced) {
      fetchActivityStats();
    } else {
      // Invalidate every badge response started before identity readiness changed.
      badgeLoadVersionRef.current += 1;
      setPortraitCompliance(null);
      setPortraitStarCount(null);
      setPortraitOwnerId(null);
      setComplianceLoaded(false);
      setComplianceLoadFailed(false);
      if (isSignedIn) return;
      // Reset all badge-related state on logout
      setComplianceWeek(0);
      setComplianceMonth(0);
      setComplianceYear(0);
      setSevenYearStarCount(0);
      setGoldYearEquivalents(0);
      setVolumePRStats({ 
        week: { prRate: 0, prs: 0, eligible: 0 },
        month: { prRate: 0, prs: 0, eligible: 0 },
        year: { prRate: 0, prs: 0, eligible: 0 }
      });
      setNutritionCompliance({ 
        week: { complianceRate: 0, compliant: 0, total: 0 },
        month: { complianceRate: 0, compliant: 0, total: 0 },
        year: { complianceRate: 0, compliant: 0, total: 0 }
      });
      setDojoCompliance(null);
    }
  }, [isSignedIn, userSynced, fetchActivityStats]);

  // Check dojo compliance status
  useEffect(() => {
    if (!isSignedIn) {
      setDojoCompliance(null);
      return;
    }
    let active = true;
    
    const checkDojoCompliance = async () => {
      try {
        const myDojo = await backendService.getMyDojo();
        if (!active) return;
        setHasDojo(Boolean(myDojo && myDojo._id));
        if (myDojo && myDojo._id) {
          const reqData = await backendService.getDojoRequirements(myDojo._id);
          if (!active) return;
          
          // Check if user was kicked
          if (reqData.kicked) {
            // Show kicked message
            alert(`You have been removed from ${reqData.compliance?.dojo_name || myDojo.name}.\n\nReason: ${reqData.compliance?.reason || 'You did not meet the membership requirements within the grace period.'}`);
            setDojoCompliance(null);
            // Refresh the page to update dojo state
            window.location.reload();
            return;
          }
          
          if (!reqData?.compliance || !reqData?.requirements) {
            setDojoCompliance(previous => previous?.dojoId === myDojo._id ? { ...previous, refreshError: true } : null);
            return;
          }
          if (!reqData.compliance.isCompliant && !reqData.compliance.compliant) {
            // Calculate remaining days from firstViolationAt
            const gracePeriodDays = reqData.compliance.gracePeriodDays || reqData.requirements?.gracePeriodDays || 7;
            const firstViolationAt = reqData.compliance.firstViolationAt;
            
            let daysRemaining = gracePeriodDays;
            if (firstViolationAt) {
              const violationStart = new Date(firstViolationAt);
              const now = new Date();
              const elapsedMs = now.getTime() - violationStart.getTime();
              const elapsedDays = elapsedMs / (24 * 60 * 60 * 1000);
              daysRemaining = Math.max(0, Math.ceil(gracePeriodDays - elapsedDays));
            }
            
            setDojoCompliance(previous => ({
              setupRetained: previous?.dojoId === myDojo._id && previous?.setupRetained,
              ...reqData.compliance,
              refreshError: false,
              gracePeriodDays,
              daysRemaining,
              firstViolationAt,
              dojoName: myDojo.name,
              dojoId: myDojo._id,
              requirements: reqData.requirements,
              violations: reqData.compliance.violations
            }));
          } else {
            // Keep the just-saved setup surface available for device diagnostics
            // until Done. A focus refresh must not erase permission instructions.
            setDojoCompliance(previous => retainDojoDeviceSetup(previous, myDojo, reqData));
          }
        } else {
          setDojoCompliance(null);
        }
      } catch (e) {
        if (!active) return;
        // Check if the error is due to being kicked (403)
        if (e.response?.status === 403 && e.response?.data?.kicked) {
          alert(`You have been removed from ${e.response.data.dojo_name}.\n\nReason: ${e.response.data.reason}`);
          setDojoCompliance(null);
          window.location.reload();
          return;
        }
        // A failed read is not evidence of compliance or membership removal.
        setDojoCompliance(previous => previous ? { ...previous, refreshError: true } : null);
      }
    };
    checkDojoCompliance();
    return () => { active = false; };
  }, [isSignedIn, backendService, dojoChangeVersion]);

  // A member may return after the chief changed rules while this tab was
  // backgrounded or its WebSocket was disconnected. Recheck on return.
  useEffect(() => {
    if (!isSignedIn) return undefined;
    const recheckOnReturn = () => {
      if (document.visibilityState === 'visible') setDojoChangeVersion(version => version + 1);
    };
    window.addEventListener('focus', recheckOnReturn);
    document.addEventListener('visibilitychange', recheckOnReturn);
    return () => {
      window.removeEventListener('focus', recheckOnReturn);
      document.removeEventListener('visibilitychange', recheckOnReturn);
    };
  }, [isSignedIn]);

  const subtractWeeks = (date, weeks) => {
    const result = new Date(date);
    result.setUTCDate(result.getUTCDate() - weeks * 7);
    return result;
  };

  const getShimmerClass = (monthRate) => {
    if (monthRate >= 100) return 'shimmer shimmer-obsidian';
    if (monthRate >= 90) return 'shimmer shimmer-gold';
    if (monthRate >= 60) return 'shimmer shimmer-silver';
    if (monthRate >= 30) return 'shimmer shimmer-bronze';
    return '';
  };

  const getFlameClass = (weekRate) => {
    if (weekRate >= 100) return 'flame-outline-platinum';
    if (weekRate >= 90) return 'flame-outline-white';
    if (weekRate >= 60) return 'flame-outline-orange';
    if (weekRate >= 30) return 'flame-outline-red';
    return '';
  };

  const getStars = (starCount) => {
    const safeCount = Number.isFinite(starCount) ? Math.floor(starCount) : 0;
    const totalStars = Math.max(0, Math.min(safeCount, 12));
    const starPoints = BADGE_STAR_POINTS;

    if (totalStars <= 0) {
      return { items: [], layout: 'none', bounds: null };
    }

    const stars = [];
    let layout = 'ring';
    let bounds = { minY: 30, maxY: 30 };

    if (totalStars <= 5) {
      layout = 'row';
      const spacing = 9;
      const startX = 30 - ((totalStars - 1) * spacing) / 2;
      const y = 56;
      const starHalfHeight = 3.5; // derived from scale factor
      bounds = { minY: y - starHalfHeight, maxY: y + starHalfHeight };
      for (let i = 0; i < totalStars; i++) {
        const x = startX + i * spacing;
        stars.push({
          points: starPoints,
          transform: `translate(${x}, ${y}) scale(3.3)`
        });
      }
    } else {
      const radius = 15;
      const centerX = 30;
      const centerY = 33;
      const starHalfHeight = 3;
      bounds = { minY: centerY - radius - starHalfHeight, maxY: centerY + radius + starHalfHeight };
      for (let i = 0; i < totalStars; i++) {
        const angle = (i / totalStars) * Math.PI * 2;
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        stars.push({
          points: starPoints,
          transform: `translate(${x.toFixed(2)}, ${y.toFixed(2)}) scale(3)`
        });
      }
    }

    return { items: stars, layout, bounds };
  };

  // Bullet point versions of badge components - simplified for small display
  const getBulletChevrons = (monthRate) => {
    const count = Math.floor(monthRate / 30);
    const chevrons = [];
    for (let i = 0; i < Math.min(count, 3); i++) {
      chevrons.push(
        <span key={i} className="bullet-chevron" style={{ 
          color: '#b8860b',
          fontSize: '12px',
          marginRight: '1px'
        }}>
          &gt;
        </span>
      );
    }
    return chevrons.length > 0 ? chevrons : [<span key="inactive" className="bullet-chevron" style={{ 
      color: '#000',
      opacity: 0.5,
      fontSize: '12px'
    }}>
      &gt;
    </span>];
  };

  const getBulletStars = (starCount) => {
    const safeCount = Number.isFinite(starCount) ? Math.floor(starCount) : 0;
    const stars = [];
    for (let i = 0; i < Math.min(Math.max(0, safeCount), 3); i++) { // Show max 3 stars in bullet
      stars.push(
        <span key={i} style={{ 
          color: '#ffd700',
          fontSize: '12px',
          marginRight: '1px'
        }}>
          ★
        </span>
      );
    }
    return stars.length > 0 ? stars : [<span key="inactive" style={{ 
      color: '#000',
      opacity: 0.5,
      fontSize: '12px'
    }}>
      ★
    </span>];
  };

  const getBulletCentralShape = yearRate => {
    const stage = getBadgeTotemStage(yearRate);
    return <BadgeTotemIcon totemKey={stage.key} label={`${stage.label} totem`} size={18} />;
  };

  const openUpdatePlanModal = (options = {}) => {
    const { planId: initialPlanId = null, entryId: focusEntryId = null } = options;
    // Indicate this modal needs the units toggle visible
    modalService.open(UpdatePlanModal, { modalService, backendService, isUsingUnits: false, initialPlanId, focusEntryId });
  };

  const portraitProgressState = !isSignedIn || !userSynced || portraitOwnerId !== clerkUser?.id
    ? 'loading'
    : complianceLoadFailed ? 'unavailable' : portraitCompliance ? 'ready' : 'loading';

  // ModalService snapshots open-time props; keep a visible owner profile current
  // as compliance and optional history settle independently (or are cleared).
  useEffect(() => {
    const portraitModal = modalService.modals[modalService.modals.length - 1];
    if (portraitModal?.ModalComponent !== UserProfileModal) return;
    const nextProps = {
      progressPercent: portraitProgressState === 'ready' ? portraitCompliance?.week ?? null : null,
      monthPercent: portraitProgressState === 'ready' ? portraitCompliance?.month ?? null : null,
      yearPercent: portraitProgressState === 'ready' ? portraitCompliance?.year ?? null : null,
      starCount: portraitProgressState === 'ready' ? portraitStarCount : null,
      progressState: portraitProgressState,
      progressUnavailable: complianceLoadFailed,
    };
    if (Object.entries(nextProps).some(([key, value]) => portraitModal.props[key] !== value)) {
      modalService.updateProps(nextProps);
    }
  }, [modalService, portraitProgressState, portraitCompliance, portraitStarCount, complianceLoadFailed]);

  const openComplianceModal = () => {
    modalService.open(ComplianceModal, { 
      modalService, 
      rates: { week: complianceWeek, month: complianceMonth, year: complianceYear },
      sevenYearStars: sevenYearStarCount,
      goldYearEquivalents,
      volumePRs: volumePRStats,
      nutritionCompliance,
      getShimmerClass,
      getCentralShape,
      getStars,
      getBulletChevrons,
      getBulletStars,
      getBulletCentralShape,
      getFlameClass
    });
  };
  const accountProfile = backendService.getUser?.() || {};
  const profileButtonAvatar = accountProfile.avatar_url || clerkUser?.imageUrl;
  const profileButtonInitial = resolveProfileButtonInitial(accountProfile, clerkUser);
  const visibleBranding = isSignedIn && userSynced && dojoBranding?.owner === clerkUser?.id && dojoBranding.status === 'ready'
    ? dojoBranding : null;

  return (
    <SubscriptionProvider backendService={backendService} isSignedIn={isSignedIn && userSynced}>
    <RecipeTimerMonitor userId={authorizedTimerUserId} />
    <ScrollToTop />
    <NativePullToRefresh />
    <Routes>
      {/* Public legal pages - no authentication required */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/refund" element={<RefundPolicyPage />} />
      <Route path="/support" element={<SupportPage />} />
      
      <Route path="/join-dojo" element={
        <DojoInvitePage backendService={backendService} isSignedIn={isSignedIn} userSynced={userSynced} />
      } />

      {/* Main app route */}
      <Route path="*" element={
        <div className="App">
          {latestAppAssets && modalService.modals.length === 0 && (
            <AppUpdateNotice onRefresh={() => reloadToLatest(latestAppAssets)} />
          )}
          {/* Loading screen with smooth exit transition */}
          {showLoadingScreen && <LoadingScreen isExiting={isLoadingExiting}
            brandingStatus={!isSignedIn && authLoaded ? 'ready' : visibleBranding?.status || 'pending'}
            dojoLogoUrl={visibleBranding?.logoUrl} dojoName={visibleBranding?.dojoName} />}
          
          {verificationMessage && (
            <div className={`verification-banner ${verificationMessage.type}`}>
              <span>{verificationMessage.text}</span>
              <button onClick={() => setVerificationMessage(null)}>×</button>
            </div>
          )}
          <AiBudgetNotice />
          {authLoaded && isSignedIn && (
            <div className="app-camera-chrome">
                <TopBar accentColor={visibleBranding?.accent}>
                  <div className="left-items">
                    {ownerReady ? <HamburgerMenu
                      onShowProfile={() => modalService.open(UserProfileModal, {
                        backendService,
                        onCoachChange: () => setDojoChangeVersion(version => version + 1),
                        onOpenCompliance: openComplianceModal,
                        onLogout: handleLogout,
                        progressPercent: portraitProgressState === 'ready' ? portraitCompliance?.week ?? null : null,
                        monthPercent: portraitProgressState === 'ready' ? portraitCompliance?.month ?? null : null,
                        yearPercent: portraitProgressState === 'ready' ? portraitCompliance?.year ?? null : null,
                        starCount: portraitProgressState === 'ready' ? portraitStarCount : null,
                        progressState: portraitProgressState,
                        progressUnavailable: complianceLoadFailed,
                      })}
                      profilePortrait={
                        <ProgressPortrait
                          user={{ ...accountProfile, avatar_url: profileButtonAvatar, display_name: accountProfile.display_name || profileButtonInitial }}
                          progressPercent={portraitProgressState === 'ready' ? portraitCompliance?.week ?? null : null}
                          monthPercent={portraitProgressState === 'ready' ? portraitCompliance?.month ?? null : null}
                          progressState={portraitProgressState}
                          size="topbar"
                        />
                      }
                      onActivityFilters={(trigger) => activityFilterRef.current?.open(trigger)}
                      activityFilterSummary={activityFilterSummary}
                      onEditPreferences={() => {
                        modalService.open(UserPreferencesModal, { backendService });
                      }}
                      onUpdatePlan={openUpdatePlanModal}
                      onShowStats={() => modalService.open(StatsModal, { backendService })}
                      onImportExport={() => modalService.open(ImportExportModal, { backendService })}
                      onShowInferences={() => modalService.open(InferencesModal, { backendService })}
                      onShowStrategy={() => modalService.open(StrategyModal, { backendService, modalService })}

                      onManageFoods={() => {
                          modalService.open(FoodsRecipesModal, { 
                              backendService,
                              modalService
                          });
                      }}
                      hasDojo={hasDojo}
                      onShowDojo={() => modalService.open(DojoHomeModal, { backendService, onDojoChange: () => setDojoChangeVersion(version => version + 1) })}
                    /> : <button type="button" onClick={handleLogout}>Sign out</button>}
                  </div>
                  <div className="topbar-activity-slot" ref={setActivityShortcutTarget} />
                  <div className="right-items">
                    <CameraSwitch camera={camera} />
                    <button 
                  className="social-top-btn"
                  disabled={!ownerReady}
                  onClick={() => {
                    modalService.open(SocialModal, { 
                      backendService,
                      streams: cameraStreamsRef.current,
                      onDojoChange: () => {
                        // SocialModal has already completed the mutation;
                        // tell the feed to re-fetch membership and activities.
                        setDojoChangeVersion(version => version + 1);
                      }
                    });
                    // Refresh unread count when modal closes
                    setTimeout(() => {
                      backendService.getUnreadMessageCount().then(setUnreadMessageCount);
                    }, 500);
                  }}
                  title={hasOngoingWorkout ? 'Social (Working Out)' : (isOnline ? 'Social (Online)' : 'Social (Offline)')}
                >
                  <UserGroupIcon style={{ width: 22, height: 22 }} />
                  <span className={`social-status-indicator ${hasOngoingWorkout ? 'working-out' : (isOnline ? 'online' : 'offline')}`}></span>
                  {unreadMessageCount > 0 && (
                    <span className="unread-pill">{unreadMessageCount > 99 ? '99+' : unreadMessageCount}</span>
                  )}
                </button>

              </div>
            </TopBar>
              <OwnerCameraArchiveContext.Provider value={cameraArchive}>
                <SharedCameraStreamsContext.Provider value={cameraStreams}>
                  <CameraPanel camera={camera} onOpenVideos={() => activityFilterRef.current?.showOnlyType('media')} />
                </SharedCameraStreamsContext.Provider>
              </OwnerCameraArchiveContext.Provider>
              <OwnerCameraArchive archive={cameraArchive} />
              <SharedCameraStreams streams={cameraStreams} playbackOnly />
            </div>
          )}
          {!loading && (
            ownerReady ? (
              <DailyStatusGate key={splashOwner} backendService={backendService} onReady={handleInitialViewReady}>
              <div className="app-content-fade-in">
                <FloatingVideo />

            <div className="app-client-zone">
              <div className="app-client-zone-content">
                <SharedCameraStreamsContext.Provider value={cameraStreams}>
                <Activities
                  backgroundImageUrl={visibleBranding?.backgroundUrl}
                  backgroundFocalPoint={visibleBranding?.focalPoint}
                  activityShortcutTarget={activityShortcutTarget}
                  activityFilterRef={activityFilterRef}
                  onActivityFilterSummary={setActivityFilterSummary}
                  todayIdentity={dojoCompliance ? <DojoComplianceBanner compliance={dojoCompliance}
                    key={dojoCompliance.dojoId}
                    backendService={backendService}
                    onUpdated={(profile, reqData) => {
                      setLocationTrackingEnabled(profile.location_tracking === true);
                      setDojoCompliance(previous => previous?.dojoId === dojoCompliance.dojoId ? {
                        ...previous,
                        ...reqData.compliance,
                        requirements: reqData.requirements,
                        violations: reqData.compliance.violations,
                        refreshError: false,
                        setupRetained: true
                      } : previous);
                      setDojoChangeVersion(version => version + 1);
                    }}
                    onDismiss={() => setDojoCompliance(previous => previous ? { ...previous, setupRetained: false } : null)}
                    onOpen={() => modalService.open(DojoRequirementsModal, {
                      backendService,
                      dojoId: dojoCompliance.dojoId,
                      dojoName: dojoCompliance.dojoName,
                      onRequirementsUpdated: () => setDojoChangeVersion(version => version + 1)
                    })} /> : null}
                  dojoChangeVersion={dojoChangeVersion}
                  onDojoChange={() => {
                    // SocialModal has already completed the mutation; tell
                    // the feed to re-fetch membership and activities.
                    setDojoChangeVersion(version => version + 1);
                  }}
                  onActivityComplete={fetchActivityStats}
                  modalService={modalService}
                  backendService={backendService}
                  onOpenPlanEditor={openUpdatePlanModal}
                  badgeLoaded={complianceLoaded}
                  onWorkoutStatusChange={setHasOngoingWorkout}
                />
                </SharedCameraStreamsContext.Provider>
              </div>
            </div>
            <UnitToggle />
          </div>
              </DailyStatusGate>
        ) : syncError ? (
          <ConnectionErrorScreen onRetry={retrySync} onSignOut={handleLogout} />
        ) : (
          showSignIn ? (
          <div className="clerk-auth-container app-content-fade-in">
            <AnimatedLogo />
            <div className="auth-content-wrapper">
              <NativeGoogleSignIn />
              <SignIn 
                appearance={{
                  baseTheme: undefined,
                  variables: {
                    colorPrimary: '#d4d4d8',
                    colorBackground: 'var(--auth-surface)',
                    colorInputBackground: '#111214',
                    colorInputText: '#f4f4f5',
                    colorText: '#f4f4f5',
                    colorTextSecondary: '#a1a1aa',
                    borderRadius: '0.65rem',
                  },
                  elements: {
                    // On native, hide Clerk's built-in social buttons and
                    // divider entirely — they would launch Google OAuth in
                    // the embedded WebView and Google blocks that. The
                    // NativeGoogleSignIn button above handles Google via
                    // Chrome Custom Tabs instead.
                    socialButtonsBlockButton: isNativePlatform()
                      ? { display: 'none' }
                      : {
                          color: '#ffffff',
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                          backgroundColor: '#202124',
                          lineHeight: '1.5',
                          '&:hover': {
                            backgroundColor: '#27282b'
                          }
                        },
                    socialButtons: isNativePlatform() ? { display: 'none' } : {},
                    dividerRow: isNativePlatform() ? { display: 'none' } : {},
                    socialButtonsBlockButtonText: {
                      color: '#ffffff',
                      fontWeight: 500
                    },
                    socialButtonsBlockButtonArrow: {
                      color: '#ffffff'
                    },
                    dividerLine: {
                      background: 'rgba(228, 228, 231, 0.12)'
                    },
                    dividerText: {
                      color: '#71717a'
                    },
                    formButtonPrimary: {
                      backgroundColor: '#f4f4f5',
                      color: '#18181b',
                      lineHeight: '1.5',
                      '&:hover': {
                        backgroundColor: '#ffffff'
                      }
                    }
                  }
                }}
              />
              <div className="auth-footer-links">
                <Link to="/pricing">Pricing</Link>
                <Link to="/terms-and-conditions">Terms</Link>
                <Link to="/privacy">Privacy</Link>
                <Link to="/refund">Refunds</Link>
              </div>
            </div>
          </div>
          ) : (
            <LandingPage onSignIn={() => setShowSignIn(true)} />
          )
        )
      )}
        </div>
      } />
    </Routes>
    </SubscriptionProvider>
  );
}

export default App;
